import { ValueOrFunction } from '../../types/general.types';
import { Coordinates, Dimensions } from '../../types/position.types';
import CoordinatesList from '../../utils/CoordinatesList';
import GraphChargeForce, {
  GraphChargeForceConfig,
} from './forces/GraphChargeForce';
import { GraphCollideForceConfig } from './forces/GraphCollideForce';
import { GraphLinksConfig } from './forces/GraphLinks';

export type GraphRenderConfig<TNodeData = Object> = {
  nodeRadius: number;
  linkWidth: number;
  linkColor: string;
  nodeColor?: string;
  nodeColorDimension?: keyof TNodeData;
  fixNodesOnDrag: boolean;
};

export const GRAPH_RENDER_CONFIG_DEFAULTS: GraphRenderConfig = {
  nodeRadius: 4,
  linkWidth: 0.5,
  linkColor: '#999999',
  nodeColor: 'black',
  nodeColorDimension: null,
  fixNodesOnDrag: false,
};

export type GraphLinkData = {
  source: number;
  target: number;
};

export type GraphNodeData<TData> = TData & {
  /**
   * Optional initial X position for the node
   */
  x?: number;

  /**
   * Optional initial Y position for the node
   */
  y?: number;
};

export type GraphData<
  TNodeData,
  TLinkData = Object,
  TNode = GraphNodeData<TNodeData>,
  TLink = TLinkData & GraphLinkData
> = {
  nodes: TNode[];
  links?: TLink[];
};

export type GraphConfig<TNodeData = {}> = GraphLinksConfig &
  GraphChargeForceConfig &
  GraphCollideForceConfig<TNodeData> & {
    gravityForce: number;
    gravityCenter: Coordinates;
    minEnergy: number;
    friction: number;
    warmupIterations: number;
    alphaDecay: number;
    alphaMin: number;
    randomizePositions: boolean;
    allowWorker: boolean;
    animate: boolean;
    ticks?: number;
    radius: ValueOrFunction<number, TNodeData>;
  };

export type GraphMessageType = 'init' | 'config';

export type WorkerGraphSetConfigValueEvent<TKey extends keyof GraphConfig> = {
  type: 'setConfigValue';
  key: TKey;
  value: GraphConfig[TKey];
};

export type WorkerGraphInitEvent<TNodeData extends Object> = {
  type: 'init';
  links: GraphLinkData[];
  nodes: TNodeData[];
  dimensions: Dimensions;
  config: GraphConfig;
  nodesRadius?: ArrayBuffer;
};

export type WorkerGraphSetDataEvent<TNodeData extends Object> = {
  type: 'setData';
  links: GraphLinkData[];
  nodes: TNodeData[];
  nodesRadius?: ArrayBuffer;
};

export type WorkerGraphConfigChangeEvent = {
  type: 'configChange';
  config: Partial<GraphConfig>;
};

export type WorkerGraphStartEvent = {
  type: 'start';
};

export type WorkerGraphResetEvent = {
  type: 'reset';
};

export type WorkerGraphFixNodePositionEvent = {
  type: 'fixNodePosition';
  nodeIndex: number;
  x: number;
  y: number;
};

export type WorkerGraphUnfixNodePositionEvent = {
  type: 'unfixNodePosition';
  nodeIndex: number;
};

export type WorkerGraphUnfixAllNodePositionsEvent = {
  type: 'unfixAllNodePositions';
};

export type WorkerGraphSetSizeEvent = {
  type: 'setSize';
  size: Dimensions;
};

export type WorkerGraphEvent<TNodeData extends Object> =
  | WorkerGraphInitEvent<TNodeData>
  | WorkerGraphConfigChangeEvent
  | WorkerGraphSetConfigValueEvent<any>
  | WorkerGraphSetDataEvent<TNodeData>
  | WorkerGraphStartEvent
  | WorkerGraphResetEvent
  | WorkerGraphFixNodePositionEvent
  | WorkerGraphUnfixNodePositionEvent
  | WorkerGraphUnfixAllNodePositionsEvent
  | WorkerGraphSetSizeEvent;

export type GraphTickEvent = {
  type: 'tick';
  positions: ArrayBuffer;
};

export type GraphEndEvent = {
  type: 'end';
};

export type MessageEventFromWorker = GraphTickEvent | GraphEndEvent;

export interface IGraphForce {
  apply: (
    positions: CoordinatesList,
    velocities: CoordinatesList,
    fixedPositions: Map<number, Coordinates>
  ) => void;
}
